import { ArrowLeft, ArrowRight, Plus } from "lucide-react";
import { useState } from "react";
import { trackAccordion } from "#/lib/analytics";
import { useI18n } from "#/lib/i18n";

/**
 * A "forward" arrow that respects reading direction: points left in RTL,
 * right in LTR. Pair with `rtl:group-hover:-translate-x-1 ltr:group-hover:translate-x-1`
 * for a direction-correct nudge on hover.
 */
export function ForwardArrow({ className = "" }: { className?: string }) {
	const { dir } = useI18n();
	const Icon = dir === "rtl" ? ArrowLeft : ArrowRight;
	return <Icon className={className} />;
}

/** Small pill that labels a section — the section eyebrow. */
export function Eyebrow({
	children,
	tone = "light",
}: {
	children: React.ReactNode;
	tone?: "light" | "dark";
}) {
	return (
		<span
			className={`inline-flex shrink-0 items-center rounded-full px-4 py-1.5 text-xs font-semibold ${
				tone === "dark"
					? "bg-white/10 text-teal-100"
					: "bg-white text-muted shadow-xs ring-1 ring-line"
			}`}
		>
			{children}
		</span>
	);
}

/**
 * Fades content up as it scrolls into view — CSS-only (see `.reveal` in
 * styles.css). Stays visible where scroll timelines aren't supported, so
 * content is never hidden behind a script that may not run.
 */
export function Reveal({
	children,
	className = "",
}: {
	children: React.ReactNode;
	className?: string;
}) {
	return <div className={`reveal ${className}`}>{children}</div>;
}

export function Accordion({
	items,
	tone = "light",
	group = "accordion",
}: {
	items: Array<{ q: string; a: string }>;
	tone?: "light" | "dark";
	/** Analytics bucket — "faq" reports as a FAQ open, anything else generic. */
	group?: string;
}) {
	const [open, setOpen] = useState<number | null>(0);
	const dark = tone === "dark";

	function toggle(index: number, isOpen: boolean) {
		setOpen(isOpen ? null : index);
		// Only expansions are interesting: which question the visitor needed
		// answered before registering.
		if (!isOpen) trackAccordion(group, index + 1, items[index].q);
	}

	return (
		<div className="flex flex-col gap-3">
			{items.map((item, i) => {
				const isOpen = open === i;
				return (
					<div
						key={item.q}
						className={`overflow-hidden rounded-2xl transition-colors ${
							dark
								? "bg-white/5 ring-1 ring-white/10"
								: "bg-white ring-1 ring-line"
						}`}
					>
						<button
							type="button"
							onClick={() => toggle(i, isOpen)}
							aria-expanded={isOpen}
							className="flex w-full items-center justify-between gap-4 px-5 py-4 text-start sm:px-6 sm:py-5"
						>
							<span
								className={`text-base font-semibold sm:text-lg ${
									dark ? "text-white" : "text-navy"
								}`}
							>
								{item.q}
							</span>
							<span
								className={`flex h-8 w-8 flex-none items-center justify-center rounded-full transition-all duration-300 ${
									isOpen ? "rotate-45 bg-teal text-white" : ""
								} ${!isOpen && dark ? "bg-white/10 text-white" : ""} ${
									!isOpen && !dark ? "bg-teal-50 text-teal" : ""
								}`}
							>
								<Plus className="h-4 w-4" />
							</span>
						</button>
						{/* grid-rows 1fr→0fr animates height without measuring it in JS */}
						<div
							className={`grid transition-all duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] ${
								isOpen
									? "grid-rows-[1fr] opacity-100"
									: "grid-rows-[0fr] opacity-0"
							}`}
						>
							<div className="overflow-hidden">
								<p
									className={`px-5 pb-5 text-sm leading-relaxed sm:px-6 sm:pb-6 ${
										dark ? "text-white/60" : "text-muted"
									}`}
								>
									{item.a}
								</p>
							</div>
						</div>
					</div>
				);
			})}
		</div>
	);
}
